import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SEO from "./components/SEO";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import PageLoader from "./components/PageLoader";
import { Toaster } from "./components/ui/toaster";
import { SiteContentProvider } from "./hooks/useSiteContent";
import { prefetchOnIntent } from "./utils/routePrefetch";

// Pages chargées à la demande — réduit le bundle initial (~60% plus léger au premier affichage)
const HomeElegant = lazy(() => import("./pages/HomeElegant"));
const Padel = lazy(() => import("./pages/Padel"));
const Loisirs = lazy(() => import("./pages/Loisirs"));
const Tournois = lazy(() => import("./pages/Tournois"));
const Restaurant = lazy(() => import("./pages/Restaurant"));
const KitchenBoard = lazy(() => import("./pages/KitchenBoard"));
const Actualites = lazy(() => import("./pages/Actualites"));
const Entreprises = lazy(() => import("./pages/Entreprises"));
const Contact = lazy(() => import("./pages/Contact"));
const Fitness = lazy(() => import("./pages/Fitness"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Credits = lazy(() => import("./pages/Credits"));
const MySubscriptions = lazy(() => import("./components/MySubscriptions"));
const MonCompte = lazy(() => import("./pages/MonCompte"));
// Réservation en ligne sur le site désactivée pour l’instant (Playtomic / contact)
const Badminton = lazy(() => import("./pages/Badminton"));
const Basket = lazy(() => import("./pages/Basket"));
const Arcade = lazy(() => import("./pages/Arcade"));
const TournoisPadel = lazy(() => import("./pages/TournoisPadel"));
const TournoisFlippers = lazy(() => import("./pages/TournoisFlippers"));
const Seminaires = lazy(() => import("./pages/Seminaires"));
const TeamBuilding = lazy(() => import("./pages/TeamBuilding"));
const Privatisation = lazy(() => import("./pages/Privatisation"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Legal = lazy(() => import("./pages/Legal"));
const Proshop = lazy(() => import("./pages/ProShop"));
const Licences = lazy(() => import("./pages/Licences"));
const LoyaltyCard = lazy(() => import("./pages/LoyaltyCard"));

const PublicShell = ({ children }) => {
  useEffect(() => {
    const warm = () => {
      prefetchOnIntent('/padel');
      prefetchOnIntent('/loisirs');
      prefetchOnIntent('/tournois');
      prefetchOnIntent('/restaurant');
      prefetchOnIntent('/proshop');
      prefetchOnIntent('/actualites');
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(warm, 2500);
    return () => clearTimeout(t);
  }, []);

  return (
  <SiteContentProvider>
    <div className="min-h-screen bg-chantilly-cream flex flex-col text-chantilly-green-dark overflow-x-hidden">
      <Header />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
      <Footer />
    </div>
  </SiteContentProvider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SEO />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/admin/dashboard" element={
              <ProtectedRoute requireAdmin={true}>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/site-content" element={<Navigate to="/admin/dashboard?tab=site" replace />} />
            <Route path="/admin/user-management" element={<Navigate to="/admin/dashboard?tab=users" replace />} />
            <Route path="/admin/court-blocking" element={<Navigate to="/admin/dashboard?tab=calendar" replace />} />
            <Route path="/admin/fitness" element={<Navigate to="/admin/dashboard?tab=fitness" replace />} />
            <Route path="/admin/coach-management" element={<Navigate to="/admin/dashboard?tab=coaches" replace />} />
            <Route path="/admin/access-control" element={<Navigate to="/admin/dashboard?tab=access" replace />} />
            <Route path="/admin/proshop" element={<Navigate to="/admin/dashboard?tab=proshop" replace />} />
            <Route path="/admin/licenses" element={<Navigate to="/admin/dashboard?tab=licenses" replace />} />
            <Route path="/admin/kitchen" element={
              <ProtectedRoute requireAdmin={true}>
                <KitchenBoard />
              </ProtectedRoute>
            } />
            <Route path="/admin-fitness" element={<Navigate to="/admin/dashboard?tab=fitness" replace />} />
            <Route path="/admin-tournaments" element={<Navigate to="/admin/dashboard?tab=tournaments" replace />} />

            <Route path="/credits" element={
              <ProtectedRoute>
                <Credits />
              </ProtectedRoute>
            } />
            <Route path="/my-subscriptions" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-chantilly-green-dark">
                  <Header />
                  <main className="container mx-auto px-4 py-8">
                    <MySubscriptions />
                  </main>
                </div>
              </ProtectedRoute>
            } />
            <Route path="/reservation" element={<Navigate to="/contact" replace />} />
            <Route path="/my-reservations" element={<Navigate to="/mon-compte" replace />} />
            <Route path="/reservations" element={<Navigate to="/contact" replace />} />

            <Route path="/mon-compte" element={<MonCompte />} />
            <Route path="/fidelite" element={
              <ProtectedRoute>
                <PublicShell>
                  <div className="container mx-auto px-4 py-10">
                    <LoyaltyCard />
                  </div>
                </PublicShell>
              </ProtectedRoute>
            } />

            <Route path="/*" element={
              <PublicShell>
                <Routes>
                  <Route path="/" element={<HomeElegant />} />
                  <Route path="/padel" element={<Padel />} />
                  <Route path="/proshop" element={<Proshop />} />
                  <Route path="/licences" element={<Licences />} />
                  <Route path="/fitness" element={<Fitness />} />
                  <Route path="/loisirs" element={<Loisirs />} />
                  <Route path="/badminton" element={<Badminton />} />
                  <Route path="/basket" element={<Basket />} />
                  <Route path="/arcade" element={<Navigate to="/loisirs" replace />} />
                  <Route path="/tournois" element={<Tournois />} />
                  <Route path="/tournois-padel" element={<TournoisPadel />} />
                  <Route path="/tournois-flippers" element={<TournoisFlippers />} />
                  <Route path="/restaurant" element={<Restaurant />} />
                  <Route path="/actualites" element={<Actualites />} />
                  <Route path="/actualites/:slug" element={<Actualites />} />
                  <Route path="/entreprises" element={<Entreprises />} />
                  <Route path="/seminaires" element={<Seminaires />} />
                  <Route path="/team-building" element={<TeamBuilding />} />
                  <Route path="/privatisation" element={<Privatisation />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/mentions-legales" element={<Legal />} />
                  <Route path="/politique-confidentialite" element={<Legal />} />
                  <Route path="/cgv" element={<Legal />} />
                </Routes>
              </PublicShell>
            } />
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
